import React from 'react';
import { useViewport } from '~/utils/viewport-provider';
import { VIEWPORT } from '~/utils/constants';
import { TextTemplate } from '~/components/reusable/TextTemplate';

function getColSpan(type?: string) {
  switch (type) {
    case 'small':
      return 'col-span-2';
    case 'medium':
      return 'col-span-3 md:col-span-2';
    case 'large':
      return 'col-span-6 md:col-span-2';
    default:
      return 'col-span-2';
  }
}

function getAlignment(align?: string) {
  switch (align) {
    case 'center':
      return 'items-center text-center';
    case 'right':
      return 'items-end text-right';
    default:
      return 'items-start text-left';
  }
}

function getLayout(image?: any, padding_bottom?: boolean) {
  const layout = {
    flow: 'flex-col',
    imageStart: false,
    imageEnd: false,
    paddingReset: '',
  };
  if (image && image.src) {
    layout.flow =
      image.position === 'left' || image.position === 'right'
        ? 'flex-row'
        : 'flex-col';
    layout.imageStart = image.position === 'top' || image.position === 'left';
    layout.imageEnd = image.position === 'bottom' || image.position === 'right';
    layout.paddingReset = '';
    if (!padding_bottom) {
      if (image.position === 'top') {
        layout.paddingReset = 'pt-0';
      } else {
        layout.paddingReset = 'pb-0';
      }
    }
  }
  return layout;
}

function isInvisible(
  device: keyof typeof VIEWPORT,
  visibility?: FeatureDeviceVisibility[]
) {
  if (!visibility) {
    return false;
  } else if (visibility.length > 0) {
    return !visibility.includes(device);
  }
  return false;
}

/** Extracted renderer to reuse in both default and special layouts */
const FeatureCard: React.FC<{
  feature: any;
  featureIndex: number;
  alignment: string;
  corners: string;
  isMobile: boolean;
  suppressOffset?: boolean;
}> = ({
  feature,
  featureIndex,
  alignment,
  corners,
  isMobile,
  suppressOffset,
}) => {
  const {
    padding_bottom = false,
    offset_top = 0,
    size_type,
    icon = { src: '', size: 0 },
    template_pretitle,
    template_title,
    template_text,
    border_settings,
    background_color = 'bg-blue-light-gradient',
    image,
  } = feature;

  const colSpan = getColSpan(size_type);
  const { flow, imageStart, imageEnd, paddingReset } = getLayout(
    image,
    padding_bottom
  );

  const featureStyle = {
    background: background_color,
    border: `${border_settings?.size || 0}px solid ${border_settings?.color || 'transparent'}`,
    marginTop: isMobile || suppressOffset ? 0 : `${offset_top || 0}rem`, // <-- respect flag
  };

  return (
    <div
      key={featureIndex}
      className={`relative flex flex-row justify-between gap-12 overflow-hidden p-10 lg:flex-col ${corners} ${flow} ${paddingReset} ${colSpan} ${alignment}`}
      style={featureStyle}
    >
      {imageStart && (
        <img
          src={image!.src}
          alt={image!.alt}
          className='h-auto max-h-[52rem] max-w-full'
          style={{
            maxWidth: image?.image_max_width
              ? `${image.image_max_width}rem`
              : '',
            maxHeight: image?.image_max_height
              ? `${image.image_max_height}rem`
              : '',
          }}
        />
      )}

      <div className='flex w-full flex-col gap-4'>
        {icon && (
          <img
            src={icon.src}
            alt=''
            style={{
              height: icon.size ? `${icon.size}rem` : 'auto',
              width: icon.size ? `${icon.size}rem` : 'auto',
            }}
            className='mb-4'
          />
        )}
        {template_pretitle && (
          <TextTemplate
            {...template_pretitle}
            as='h3'
          />
        )}
        {template_title && (
          <TextTemplate
            {...template_title}
            as='h3'
          />
        )}
        {template_text && (
          <TextTemplate
            {...template_text}
            as='p'
          />
        )}
      </div>

      {imageEnd && (
        <img
          src={image!.src}
          alt={image!.alt}
          className='mx-auto h-auto max-h-[52rem] max-w-full'
          style={{
            maxWidth: image?.image_max_width
              ? `${image.image_max_width}rem`
              : '',
            maxHeight: image?.image_max_height
              ? `${image.image_max_height}rem`
              : '',
          }}
        />
      )}
    </div>
  );
};

export const FeaturesGrid: React.FC<FeaturesGrid> = ({
  content_alignment,
  rounded_corners,
  features_grid,
}) => {
  const { viewport, isMobile } = useViewport();

  const alignment = getAlignment(content_alignment);
  const corners =
    typeof rounded_corners === undefined || rounded_corners === ''
      ? 'border-rounded-strong'
      : rounded_corners;

  // Filter by device visibility FIRST
  const visibleFeatures = features_grid.filter(
    f => !isInvisible(viewport.device, f.visibility)
  );

  // -------- 7-item special layout: [2 | 3 | 2] --------
  if (visibleFeatures.length === 7) {
    const colA = visibleFeatures.slice(0, 2);
    const colB = visibleFeatures.slice(2, 5);
    const colC = visibleFeatures.slice(5, 7);

    return (
      <div className='grid max-w-xl grid-cols-3 gap-6 xl:max-w-lg lg:max-w-md md:grid-cols-1 md:px-4'>
        {/* First column: two tiles centered vertically */}
        <div className='flex min-h-[70vh] flex-col justify-center gap-6 md:min-h-0 md:justify-start'>
          {colA.map((feature, i) => (
            <FeatureCard
              key={`colA-${i}`}
              feature={feature}
              featureIndex={i}
              alignment={alignment}
              corners={corners || ''}
              isMobile={isMobile}
              suppressOffset
            />
          ))}
        </div>

        {/* Middle column: three tiles spaced top/middle/bottom */}
        <div className='flex min-h-[70vh] flex-col justify-between gap-6 md:min-h-0 md:justify-start'>
          {colB.map((feature, i) => (
            <FeatureCard
              key={`colB-${i}`}
              feature={feature}
              featureIndex={i + 2}
              alignment={alignment}
              corners={corners || ''}
              isMobile={isMobile}
              suppressOffset
            />
          ))}
        </div>

        {/* Last column: two tiles centered vertically */}
        <div className='flex min-h-[70vh] flex-col justify-center gap-6 md:min-h-0 md:justify-start'>
          {colC.map((feature, i) => (
            <FeatureCard
              key={`colC-${i}`}
              feature={feature}
              featureIndex={i + 5}
              alignment={alignment}
              corners={corners || ''}
              isMobile={isMobile}
              suppressOffset
            />
          ))}
        </div>
      </div>
    );
  }

  // -------- Default layout (unchanged) --------
  return (
    <div
      className={`grid max-w-xl grid-cols-6 items-start justify-between gap-6 xl:max-w-lg lg:max-w-md md:grid-cols-2 md:px-4`}
    >
      {visibleFeatures.map((feature, featureIndex) => (
        <FeatureCard
          key={featureIndex}
          feature={feature}
          featureIndex={featureIndex}
          alignment={alignment}
          corners={corners || ''}
          isMobile={isMobile}
          suppressOffset={false}
        />
      ))}
    </div>
  );
};
